import React, { forwardRef } from 'react';

import type { TextInput as RNTextInput } from 'react-native';

import { ValidatedTextInput } from '../inputs';
import type { ValidatedTextInputProps } from '../inputs';

export interface EmailInputProps extends Omit<
  ValidatedTextInputProps,
  'format' | 'customErrorMessages' | 'ariaLabelledBy' | 'ariaLabel'
> {
  /**
   * Analytics name for tracking events
   */
  analyticsName: string;
  /**
   * Custom error message for required field
   * @default "This field is required."
   */
  fieldRequiredError?: string;
  /**
   * Custom error message for invalid email
   * @default "Please enter a valid email address."
   */
  invalidEmailError?: string;
  /**
   * Whether to disable email suggestions
   * @default false
   */
  disableSuggestions?: boolean;
  /**
   * ARIA labelledBy attribute
   */
  ariaLabelledBy?: string;
}

/**
 * EmailInput is a specialized email input component with validation and suggestions.
 * It extends ValidatedTextInput with email-specific validation and configuration.
 *
 * Features:
 * - Email validation using ValidatedTextInput's built-in format
 * - Email suggestions (configurable)
 * - Customizable error messages
 * - Consistent styling
 *
 * @example
 * ```tsx
 * <EmailInput
 *   analyticsName="my-app-login"
 *   title="Email"
 *   getValue={(value) => setEmail(value.unformattedValue)}
 *   onValidationChange={(validity) => setIsValid(validity === 'valid')}
 * />
 * ```
 */
export const EmailInput = forwardRef<RNTextInput, EmailInputProps>(
  (
    {
      analyticsName,
      fieldRequiredError = 'This field is required.',
      invalidEmailError = 'Please enter a valid email address.',
      disableSuggestions = false,
      title = 'Email',
      size = 'medium',
      required = true,
      ariaLabelledBy,
      ...props
    },
    ref,
  ) => {
    return (
      <ValidatedTextInput
        ref={ref}
        id="email-login-input"
        analyticsName={`${analyticsName}-email`}
        format="emailAddress"
        size={size}
        required={required}
        allowSuggestions={!disableSuggestions}
        customErrorMessages={{
          fieldRequired: fieldRequiredError,
          invalidEmail: invalidEmailError,
        }}
        {...(ariaLabelledBy ? { ariaLabelledBy } : { title })}
        {...props}
      />
    );
  },
);

EmailInput.displayName = 'EmailInput';
